import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader } from '../../utils/icons';
import { DocumentViewer } from '../../components/DocumentViewer';
import { useFilePreview } from '../../hooks/useDrive';
import { AUTH_KEYS } from '../../api/axios-instance';
import { driveApi } from '../../api/drive-api';
import type { Document } from '../../types';
import type { DriveFilePreview } from '../../types/drive';

const DownloadIcon = Download as React.ComponentType<{ className?: string; size?: number }>;
const LoaderIcon = Loader as React.ComponentType<{ className?: string; size?: number }>;

const LEGAL_FORMS_PLACEHOLDER_USER = {
  id: 'legal-forms',
  username: 'Legal Forms',
  email: '',
  initials: 'LF',
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Build a Document for DocumentViewer from drive preview and optional blob URL */
function previewToDocument(preview: DriveFilePreview, blobUrl: string | null): Document {
  return {
    id: preview.id,
    name: preview.name,
    type: preview.mimeType || 'document',
    uploadedBy: LEGAL_FORMS_PLACEHOLDER_USER,
    uploadedAt: preview.modifiedTime,
    size: preview.size ?? undefined,
    url: blobUrl ?? undefined,
  };
}

const LegalFormsPreviewPage: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const { data: preview, isLoading, isError } = useFilePreview(fileId || '');

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobError, setBlobError] = useState<string | null>(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem(AUTH_KEYS.accessToken) : null;

  const isDocx =
    Boolean(preview) &&
    (preview!.name.toLowerCase().endsWith('.docx') ||
      preview!.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');

  // Fetch blob for PDF/image/DOCX so we can show in DocumentViewer.
  // (DOCX text extraction for chat is not supported yet; it will fall back to metadata-only answers.)
  useEffect(() => {
    if (!preview || !(preview.previewType === 'pdf' || preview.previewType === 'image' || isDocx)) {
      setBlobUrl(null);
      setBlobError(null);
      return;
    }
    let revoked = false;
    setBlobError(null);
    const fetchBlob =
      preview.previewType === 'pdf' && preview.exportPdfUrl
        ? () => driveApi.getExportPdfBlob(preview.id, token || undefined)
        : () => driveApi.downloadFileBlob(preview.id, token || undefined);
    fetchBlob()
      .then((blob) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!revoked) setBlobError('Failed to load file');
      });
    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [preview?.id, preview?.previewType, preview?.exportPdfUrl, preview?.name, preview?.mimeType, token, isDocx]);

  const downloadWithAuth = async () => {
    if (!preview) return;
    const blob = await driveApi.downloadFileBlob(preview.id, token || undefined);
    downloadBlob(blob, preview.name);
  };

  const document = useMemo(() => {
    if (!preview) return null;
    // For pdf/image/docx use blob URL when ready; for other types no url.
    const url =
      preview.previewType === 'pdf' || preview.previewType === 'image' || isDocx ? blobUrl : null;
    return previewToDocument(preview, url);
  }, [preview, blobUrl, isDocx]);

  const blobLoading =
    preview &&
    (preview.previewType === 'pdf' || preview.previewType === 'image' || isDocx) &&
    !blobUrl &&
    !blobError;

  // Loading preview metadata
  if (isLoading && !preview) {
    return (
      <div className="flex items-center justify-center py-32">
        <LoaderIcon size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  // Error loading preview
  if (isError && !preview) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-slate-800/50 rounded-2xl">
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Could not load preview</p>
      </div>
    );
  }

  // No preview data
  if (!preview) {
    return null;
  }

  // Loading blob for pdf/image
  if (blobLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <LoaderIcon size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  // Blob fetch error for pdf/image
  if (blobError && (preview.previewType === 'pdf' || preview.previewType === 'image' || isDocx)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-slate-800/50 rounded-2xl">
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{blobError}</p>
        <button
          type="button"
          onClick={downloadWithAuth}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
        >
          <DownloadIcon size={14} />
          Download Instead
        </button>
      </div>
    );
  }

  // Show DocumentViewer (with or without url for download-only)
  return (
    <div className="-m-6 -mx-6 xl:-mx-0">
      <DocumentViewer
        document={document!}
        backHref="/dashboard/legal-forms"
        editHref={null}
        onDelete={null}
        onDownload={downloadWithAuth}
        hideDownload
      />
    </div>
  );
};

export default LegalFormsPreviewPage;
